import { Component, EventEmitter, h, State, Event, Prop, Watch } from '@stencil/core';
import store from '../../../helpers/store';
import { FlowStatus } from '../../../models/FlowStatus';
import { FlowSteps } from '../../../models/FlowSteps';
import { BaseComponent } from '../../base-component';
import { TranslationValues } from '../../../models/ITranslationValues';
import { Translations } from '../../../helpers/TranslationUtils';
import { ApiCall } from '../../../helpers/ApiCall';
import { CountryData, DEFAULT_COUNTRY_CODE, getFilteredCountries, validatePhoneNumber, getFullPhoneNumber, parsePhoneNumber } from '../../../data/european-countries';
// @ts-ignore
import loaderImg from '../../../assets/loader.svg';
import logoOntrace from '../../../assets/logo-ontrace.svg';
import icoPhone from '../../../assets/landing/ico-phone.svg';
import { db } from '../../../helpers/db';

/**
 * Validation error event detail interface
 */
export interface PhoneValidationError {
  errorType: 'length' | 'mobilePrefix' | 'format' | 'unknown';
  message: string;
  phoneNumber: string;
  countryCode: string;
}

@Component({
  tag: 'sms-send',
  styleUrl: 'sms-send.css',
})
export class SmsSend {
  /**
   * Array of allowed country codes (ISO 3166-1 alpha-2).
   * If provided, only these countries will be shown in the dropdown.
   * If null/undefined, all European countries are available.
   * Example: ["RO", "MD", "BG"]
   */
  @Prop() allowedPhoneCountries?: string[] | null;

  /**
   * Default country code to use as fallback when IP detection fails.
   * If not provided, defaults to "RO" (Romania).
   */
  @Prop() defaultCountry: string = DEFAULT_COUNTRY_CODE;

  /**
   * Phone number passed to the component at initialization.
   * Can include country prefix (e.g., "+40712345678") or be without prefix.
   */
  @Prop() initialPhoneNumber?: string;

  @State() private buttonText: string;
  @State() private title: string;
  @State() private details: string;
  @State() private phoneNumber: string = '';
  @State() private prefilledPhone: boolean = false;

  @State() private canSend: boolean = false;
  @State() private sending: boolean = false;

  /**
   * Currently selected country for phone prefix
   */
  @State() private selectedCountry: CountryData | null = null;

  /**
   * Validation error message to display
   */
  @State() private validationError: string = '';

  /**
   * Whether the component has finished initial loading
   */
  @State() private isLoading: boolean = true;

  /**
   * Full phone number with country prefix (for display)
   */
  @State() private fullPhoneNumber: string = '';

  private translations: TranslationValues;

  @Event({
    eventName: 'apiError',
  })
  apiErrorEvent: EventEmitter<any>;

  /**
   * Event emitted when phone validation fails.
   * Useful for parent components to handle validation errors.
   */
  @Event({
    eventName: 'validationError',
    bubbles: true,
    composed: true,
  })
  validationErrorEvent: EventEmitter<PhoneValidationError>;

  /**
   * Event emitted when a valid phone number is entered.
   * Contains the full phone number with country prefix.
   */
  @Event({
    eventName: 'phoneNumberChanged',
    bubbles: true,
    composed: true,
  })
  phoneNumberChangedEvent: EventEmitter<string>;

  constructor() {}

  @Watch('phoneNumber')
  @Watch('selectedCountry')
  watchPhoneValidation() {
    this.validateAndUpdateState();
  }

  private async doAction() {
    if (this.sending) return;
    this.sending = true;
    this.canSend = false;

    try {
      const phoneToSend = this.fullPhoneNumber;
      const codeSent = await ApiCall.instance.SendOTPCode(store.requestId, phoneToSend);
      if (codeSent.sent === true) {
        await this.setPhoneNumber();
        store.flowStatus = FlowStatus[codeSent.state];
      }
    } catch (e) {
      this.sending = false;
      this.canSend = true;
      this.apiErrorEvent.emit(e);
    }
  }

  private async setPhoneNumber() {
    const ectData = await db.ectdata.get(store.requestId);
    ectData.phoneNumber = this.fullPhoneNumber;
    await db.ectdata.put(ectData);
  }

  async componentWillLoad() {
    // Initial state setup before first render
  }

  async componentWillRender() {
    try {
      this.translations = await Translations.getValues();
      if (this.translations?.PhoneValidationValues) {
        this.buttonText = this.translations.PhoneValidationValues.Button;
        this.title = this.translations.PhoneValidationValues.Title;
        this.details = this.translations.PhoneValidationValues.Description;
      }
    } catch {
      // Use fallback values when translations are not available (standalone mode)
    }

    if (!this.buttonText) this.buttonText = 'Send SMS';
    if (!this.title) this.title = 'Phone Verification';
    if (!this.details) this.details = 'Enter your phone number to receive a verification code';
  }

  async componentDidLoad() {
    try {
      await BaseComponent.initialize(FlowSteps.OtpSend);
    } catch (e) {
      console.warn('SmsSend: BaseComponent.initialize failed', e);
    }

    await this.initializeCountrySelection();
    await this.initializePhoneNumber();
    this.isLoading = false;
  }

  async disconnectedCallback() {
    await BaseComponent.finalize(FlowSteps.OtpSend);
  }

  private async initializeCountrySelection() {
    const allowedCountries = getFilteredCountries(this.allowedPhoneCountries);

    if (allowedCountries.length === 0) {
      console.warn('SmsSend: No allowed countries available');
      return;
    }

    // Use detected country from backend (set during identity/insert)
    const detectedCountryCode = store.detectedCountryCode || this.defaultCountry;
    const detectedCountry = allowedCountries.find(c => c.code.toUpperCase() === detectedCountryCode.toUpperCase());

    if (detectedCountry) {
      this.selectedCountry = detectedCountry;
    } else {
      // Fallback to default country if detected country is not in allowed list
      const defaultCountry = allowedCountries.find(c => c.code.toUpperCase() === this.defaultCountry.toUpperCase());

      if (defaultCountry) {
        this.selectedCountry = defaultCountry;
      } else {
        this.selectedCountry = allowedCountries[0];
      }
    }
  }

  private async initializePhoneNumber() {
    let initialNumber = '';

    if (this.initialPhoneNumber && this.initialPhoneNumber.trim() !== '') {
      initialNumber = this.initialPhoneNumber.trim();
    } else if (store.phoneNumber && store.phoneNumber.trim() !== '') {
      initialNumber = store.phoneNumber.trim();
    }

    if (initialNumber) {
      const parsed = parsePhoneNumber(initialNumber);

      if (parsed) {
        const allowedCountries = getFilteredCountries(this.allowedPhoneCountries);
        const isAllowed = allowedCountries.some(c => c.code === parsed.country.code);

        if (isAllowed) {
          this.selectedCountry = parsed.country;
          this.phoneNumber = parsed.localNumber;
        } else {
          this.phoneNumber = initialNumber.replace(/\D/g, '');
        }
      } else {
        this.phoneNumber = initialNumber.replace(/\D/g, '');
      }

      this.prefilledPhone = true;
      this.validateAndUpdateState();

      if (!this.canSend && this.validationError) {
        this.emitValidationError();
      }
    }
  }

  private validateAndUpdateState() {
    if (!this.selectedCountry) {
      this.canSend = false;
      this.validationError = '';
      this.fullPhoneNumber = '';
      return;
    }

    this.fullPhoneNumber = getFullPhoneNumber(this.phoneNumber, this.selectedCountry);

    if (this.phoneNumber) {
      this.phoneNumberChangedEvent.emit(this.fullPhoneNumber);
    }

    if (!this.phoneNumber || this.phoneNumber.length === 0) {
      this.canSend = false;
      this.validationError = '';
      return;
    }

    const validation = validatePhoneNumber(this.phoneNumber, this.selectedCountry);

    if (validation.isValid) {
      this.canSend = true;
      this.validationError = '';
    } else {
      this.canSend = false;
      this.validationError = validation.error || 'Invalid phone number';
    }
  }

  private emitValidationError() {
    if (!this.selectedCountry) return;

    let errorType: PhoneValidationError['errorType'] = 'unknown';

    if (this.validationError.includes('too short') || this.validationError.includes('too long')) {
      errorType = 'length';
    } else if (this.validationError.includes('prefix')) {
      errorType = 'mobilePrefix';
    }

    this.validationErrorEvent.emit({
      errorType,
      message: this.validationError,
      phoneNumber: this.fullPhoneNumber,
      countryCode: this.selectedCountry.code,
    });
  }

  handleChangePhone(ev: Event) {
    const target = ev.target as HTMLInputElement;
    let value = target ? target.value : '';
    const cleanValue = value.replace(/\D/g, '');

    if (this.selectedCountry) {
      const prefixLength = this.selectedCountry.prefix.replace('+', '').length;
      const maxLocalLength = this.selectedCountry.lengthMax - prefixLength - 1;
      this.phoneNumber = cleanValue.substring(0, maxLocalLength);
    } else {
      this.phoneNumber = cleanValue.substring(0, 15);
    }

    target.value = this.phoneNumber;
  }

  handleCountrySelect(event: CustomEvent<CountryData>) {
    this.selectedCountry = event.detail;
    this.validateAndUpdateState();
  }

  render() {
    const allowedCountries = this.allowedPhoneCountries;

    return (
      <div class="container flex center">
        <div class="px-1">
          <div class="mb-1 ml-1 text-center">
            <img src={icoPhone} style={{ width: '60px' }}></img>
          </div>
          <h1 class="text-center px-1 mb-2">{this.title}</h1>
          <p class="text-center mb-05">{this.details}</p>

          <div class="phone-input-container mb-1">
            <div class="phone-input-wrapper">
              <country-selector
                selectedCode={this.selectedCountry?.code || ''}
                allowedCountries={allowedCountries}
                searchPlaceholder={this.translations?.PhoneValidationValues?.SearchCountry || 'Search country...'}
                disabled={this.prefilledPhone || this.isLoading}
                onCountrySelected={(ev: CustomEvent<CountryData>) => this.handleCountrySelect(ev)}
              ></country-selector>

              <input
                placeholder={this.translations?.PhoneValidationValues?.Label || 'Phone number'}
                type="tel"
                id="phoneInput"
                class={`phone-input ${this.validationError ? 'has-error' : ''}`}
                disabled={this.prefilledPhone}
                onInput={ev => this.handleChangePhone(ev)}
                value={this.phoneNumber}
                aria-describedby="phoneError fullPhoneDisplay"
              ></input>
            </div>

            {this.validationError && (
              <div id="phoneError" class="validation-error" role="alert">
                {this.validationError}
              </div>
            )}

            {this.phoneNumber && this.selectedCountry && (
              <div id="fullPhoneDisplay" class="full-phone-display">
                <span class="full-phone-label">{this.translations?.PhoneValidationValues?.FullNumber || 'Full number:'}</span>
                <span class="full-phone-number">{this.fullPhoneNumber}</span>
              </div>
            )}
          </div>

          <div class="button-container mb-1">
            {this.sending ? (
              <div class="img-loader rounded img-loader-button">
                <img class="loading-image loading-image-button" src={loaderImg}></img>
              </div>
            ) : null}
            <button id="action" disabled={!this.canSend} class="main-button" onClick={() => this.doAction()}>
              {this.buttonText}
            </button>
          </div>
          <div class="footer text-center">
            <img src={logoOntrace}></img>
          </div>
        </div>
      </div>
    );
  }
}
